import { basename, pathSegments } from './desktop-paths';

/*
 * The sidebar's projects: one per logical workspace (a repository and its worktrees, on this
 * machine and the cloud alike), named by the server's name for it. Folders the server does not
 * know yet are projects of their own, by path. Two projects of one name (two repositories called
 * `app`) stay apart, each label saying which folder it is.
 */

export type SidebarProjectWorkspace = {
  id: string;
  logicalWorkspaceId?: string;
  rootPath: string;
  name: string;
  runtime?: 'local' | 'worktree' | 'cloud';
  /** For a worktree, the repository checkout it belongs to. */
  sourceRoot?: string | null;
  availableOnDevice?: boolean;
};

/**
 * A project: its identity, its folder (the repository checkout, not a worktree: this machine's
 * before a cloud copy), every folder it was seen in, in order, and its label.
 */
export type SidebarProject = {
  key: string;
  root: string | null;
  roots: readonly string[];
  label: string;
};

export type SidebarProjectDirectory = {
  /** The project of a folder (a worktree's is its repository's); `null`: none. */
  ofRoot: (root: string | null) => SidebarProject;
  /** The project of a recorded workspace, by its id, or of `fallbackRoot` when unknown. */
  ofWorkspace: (workspaceId: string, fallbackRoot: string | null) => SidebarProject;
  /** Every project listed (a folder gone from this device lists none of its own). */
  all: () => readonly SidebarProject[];
};

const NO_PROJECT_KEY = 'none';
const NO_PROJECT: SidebarProject = {
  key: NO_PROJECT_KEY,
  root: null,
  roots: [],
  label: 'No project',
};

type Identity = { key: string; root: string; name: string };

const isCloudCopy = (root: string) => root.startsWith('/workspace/');

/** A record's project identity: its logical workspace, its repository's folder, its name. */
function identityOf(record: SidebarProjectWorkspace): Identity {
  const root = record.sourceRoot || record.rootPath;
  return {
    key: record.logicalWorkspaceId ?? `path:${root}`,
    root,
    name: record.name || basename(root),
  };
}

/**
 * Builds the projects of the sidebar from the server's workspace records and the folders open
 * (`roots`). `projectName` names a folder's project as the shell shows it (the personal
 * workspace, a cloud repository's full name), from the server's name.
 */
export function buildProjectDirectory(input: {
  workspaces: readonly SidebarProjectWorkspace[];
  roots?: readonly (string | null)[];
  projectName: (root: string | null, fallback: string) => string;
}): SidebarProjectDirectory {
  const { workspaces, projectName } = input;
  const listed = new Map<string, { identity: Identity; roots: string[] }>();
  const byRoot = new Map<string, Identity>();
  const byId = new Map<string, Identity>();
  const list = (identity: Identity) => {
    const known = listed.get(identity.key);
    if (known === undefined) listed.set(identity.key, { identity, roots: [identity.root] });
    else if (!known.roots.includes(identity.root)) known.roots.push(identity.root);
  };
  for (const record of workspaces) {
    const identity = identityOf(record);
    byId.set(record.id, identity);
    if (!byRoot.has(record.rootPath)) byRoot.set(record.rootPath, identity);
    // A folder gone from this device files its conversations, but lists no project of its own.
    if (record.availableOnDevice !== false) list(identity);
  }
  const identityOfRoot = (root: string): Identity =>
    byRoot.get(root) ?? { key: `path:${root}`, root, name: basename(root) };
  for (const root of input.roots ?? []) if (root !== null) list(identityOfRoot(root));

  const folderOf = (key: string, fallback: Identity) => {
    const roots = listed.get(key)?.roots ?? [fallback.root];
    return { roots, root: roots.find((root) => !isCloudCopy(root)) ?? roots[0] ?? fallback.root };
  };
  // Labels: the shell's name for each project, told apart by its parent folder when two share it.
  const plain = new Map<string, string>();
  for (const [key, { identity }] of listed) {
    const { root } = folderOf(key, identity);
    plain.set(key, basename(projectName(root, identity.name)) || identity.name);
  }
  const counts = new Map<string, number>();
  for (const label of plain.values()) counts.set(label, (counts.get(label) ?? 0) + 1);
  const labels = new Map<string, string>();
  const taken = new Set<string>();
  for (const [key, { identity }] of listed) {
    const name = plain.get(key) ?? identity.name;
    let label = name;
    if ((counts.get(name) ?? 0) > 1) {
      const parent = pathSegments(folderOf(key, identity).root).at(-2);
      if (parent) label = `${parent}/${name}`;
    }
    for (let index = 2; taken.has(label); index += 1) label = `${name} (${index})`;
    taken.add(label);
    labels.set(key, label);
  }

  const project = (identity: Identity): SidebarProject => {
    const known = listed.get(identity.key)?.identity ?? identity;
    const { root, roots } = folderOf(identity.key, identity);
    return {
      key: identity.key,
      root,
      roots,
      label: labels.get(identity.key) ?? basename(projectName(root, known.name)),
    };
  };
  return {
    ofRoot: (root) => (root === null ? NO_PROJECT : project(identityOfRoot(root))),
    ofWorkspace: (workspaceId, fallbackRoot) => {
      const identity = byId.get(workspaceId);
      if (identity !== undefined) return project(identity);
      return fallbackRoot === null ? NO_PROJECT : project(identityOfRoot(fallbackRoot));
    },
    all: () => [...listed.values()].map(({ identity }) => project(identity)),
  };
}
